import React from 'react';
import { MapPin } from 'lucide-react';
/**
 * Static, non-interactive grayscale street map centred on a service city.
 *
 * Built as a mosaic of raster map tiles rather than a Leaflet instance on
 * purpose: this is a piece of page furniture, not a tool. A live map in a page
 * header invites panning and zooming, swallows scroll gestures on a phone, and
 * pulls in a library plus its CSS for something the visitor never touches. The
 * real interactive map lives on /delivery-zones, which this links to.
 *
 * Tiles come from OpenStreetMap's own tile server — the only major basemap
 * that still needs no API key or account — and are desaturated in CSS, so the
 * artwork reads as a quiet monochrome plate behind the city name instead of
 * competing with the page's green accents. Only 27 city pages exist and each
 * draws a couple of dozen lazy-loaded tiles, which sits comfortably inside
 * OSM's usage policy; a keyed provider would be the move if that grew.
 */
type CityMiniMapProps = {
  /** [latitude, longitude] of the city centre. */
  coords: [number, number];
  /** City name, rendered on the plate. */
  name: string;
  /** Two-letter state code, rendered beside the name. */
  state: string;
  /** Slippy-map zoom. 12 frames a city; 13 frames a neighbourhood. */
  zoom?: number;
  className?: string;
};
const TILE_SIZE = 256;
// Tiles are drawn in a (2 * TILE_RADIUS + 1) square around the centre tile.
// Two rings covers a 1280px box, which is wider and taller than this plate
// ever renders, so no blank corners appear at any container size.
const TILE_RADIUS = 2;

/** Longitude → fractional tile X (Web Mercator). */
function lonToTileX(lon: number, zoom: number): number {
  return (lon + 180) / 360 * Math.pow(2, zoom);
}

/** Latitude → fractional tile Y (Web Mercator). */
function latToTileY(lat: number, zoom: number): number {
  const rad = lat * Math.PI / 180;
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, zoom);
}
export function CityMiniMap({
  coords,
  name,
  state,
  zoom = 12,
  className = ''
}: CityMiniMapProps) {
  const [lat, lon] = coords;

  // Centre position in absolute world pixels at this zoom.
  const centerX = lonToTileX(lon, zoom) * TILE_SIZE;
  const centerY = latToTileY(lat, zoom) * TILE_SIZE;
  const centerTileX = Math.floor(centerX / TILE_SIZE);
  const centerTileY = Math.floor(centerY / TILE_SIZE);
  const tiles: {
    key: string;
    src: string;
    dx: number;
    dy: number;
  }[] = [];
  const maxTile = Math.pow(2, zoom);
  for (let ox = -TILE_RADIUS; ox <= TILE_RADIUS; ox++) {
    for (let oy = -TILE_RADIUS; oy <= TILE_RADIUS; oy++) {
      const tx = centerTileX + ox;
      const ty = centerTileY + oy;
      // Vertical wrap is meaningless (no tiles above the pole), so skip
      // rather than request a 404.
      if (ty < 0 || ty >= maxTile) continue;
      // Horizontal wrap is valid — the world repeats east/west.
      const wrappedX = (tx % maxTile + maxTile) % maxTile;
      tiles.push({
        key: `${tx}-${ty}`,
        // OpenStreetMap's own tile server: no key, no signup, no watermark.
        // CARTO's basemap CDN now returns an "API KEY REQUIRED" placeholder
        // tile for unregistered origins, which is why it can't be used here.
        // The tiles arrive in full colour and are desaturated in CSS below.
        src: `https://tile.openstreetmap.org/${zoom}/${wrappedX}/${ty}.png`,
        // Offset of this tile's top-left corner from the centre point.
        dx: tx * TILE_SIZE - centerX,
        dy: ty * TILE_SIZE - centerY
      });
    }
  }
  return <div className={`relative overflow-hidden bg-stone-100 dark:bg-white/5 ${className}`}
  // The map is decorative reinforcement of the city name already in the
  // H1 — announcing a wall of tile images would only add noise.
  aria-hidden="true" data-id="element-742" data-mp="25jja5qrp44jc">
      <div className="absolute inset-0 grayscale contrast-[1.08] brightness-[1.02] dark:opacity-40 dark:invert" data-id="element-743" data-mp="2y7f4wpxpfmdh">
        {tiles.map(tile => <img key={tile.key} src={tile.src} alt="" width={TILE_SIZE} height={TILE_SIZE} loading="lazy" decoding="async" draggable={false} className="pointer-events-none absolute select-none" style={{
        left: `calc(50% + ${tile.dx}px)`,
        top: `calc(50% + ${tile.dy}px)`
      }} data-id="element-744" data-mp="1fc6fi96xtv3p" />)}
      </div>

      {/* Left-to-right scrim: fully the page background on the left edge,
          clearing to nothing on the right. The map emerges out of the copy
          column rather than sitting in a framed box, so no border or corner
          radius is needed to contain it. */}
      <div className="absolute inset-0 dark:hidden" style={{
      background: 'linear-gradient(to right, #faf9f5 0%, rgba(250,249,245,0) 100%)'
    }} data-id="element-745" data-mp="1ocbk6bkqyfe6" />
      <div className="absolute inset-0 hidden dark:block" style={{
      background: 'linear-gradient(to right, #171717 0%, rgba(23,23,23,0) 100%)'
    }} data-id="element-746" data-mp="1jcp7c38tz15w" />

      {/* Centre marker + place label. The label is what makes the plate read as
          "this city" rather than generic map texture. */}
      <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center" data-id="element-747" data-mp="2kc4m1itg87vt">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#05894e] shadow-[0_2px_10px_rgba(0,0,0,0.25)]" data-id="element-748" data-mp="2ocf1dm34sanc">
          <MapPin className="h-4 w-4 text-white" strokeWidth={2.5} data-id="element-749" data-mp="2yabcidw2j5lg" />
        </span>
        <span className="mt-2 whitespace-nowrap text-[11px] font-bold uppercase tracking-[0.16em] text-stone-700 dark:text-stone-200" data-id="element-750" data-mp="2y7ykt90idgv7">
          {name}, {state}
        </span>
      </div>

      {/* Tile attribution. Required by CARTO/OSM terms, kept deliberately
          quiet — it is a licence notice, not content. */}
      <span className="absolute bottom-1 right-2 text-[9px] leading-none text-stone-400 dark:text-stone-500" data-id="element-751" data-mp="3h0j90d59ixso">
        © OpenStreetMap contributors
      </span>
    </div>;
}